/**
 * PopularityBadge Component
 * 
 * Displays a compact popularity tier + velocity indicator.
 * Example: "Rare ↑" or "Commonplace ↓↓"
 * 
 * Props:
 * - popularity: 1-100 score
 * - velocity: 'significantly_decreasing' | 'decreasing' | 'static' | 'increasing' | 'significantly_increasing'
 * - compact: boolean (optional, for smaller displays)
 */

import React from 'react';

// Map 1-100 score to 7 tier labels - MUTED WINE PALETTE
const getTier = (popularity) => {
    const score = Number(popularity) || 50;
    // All tiers use wine/amber tones for thematic consistency
    if (score >= 85) return { label: 'Commonplace', color: 'text-gray-400', bg: 'bg-gray-500/10' };
    if (score >= 70) return { label: 'Very Popular', color: 'text-gray-300', bg: 'bg-gray-500/10' };
    if (score >= 55) return { label: 'Popular', color: 'text-amber-500/80', bg: 'bg-amber-500/5' };
    if (score >= 40) return { label: 'Average', color: 'text-dark-400', bg: 'bg-dark-700/50' };
    if (score >= 25) return { label: 'Uncommon', color: 'text-wine-400/70', bg: 'bg-wine-500/5' };
    if (score >= 10) return { label: 'Rare', color: 'text-wine-400', bg: 'bg-wine-500/10' };
    return { label: 'Very Rare', color: 'text-wine-300', bg: 'bg-wine-500/15' };
};

// Map velocity to arrow symbol - muted indicators
const getVelocity = (velocity) => {
    switch (velocity) {
        case 'significantly_increasing':
            return { symbol: '↑↑', color: 'text-amber-500/80', animate: '' };
        case 'increasing':
            return { symbol: '↑', color: 'text-amber-500/60', animate: '' };
        case 'decreasing':
            return { symbol: '↓', color: 'text-wine-400/60', animate: '' };
        case 'significantly_decreasing':
            return { symbol: '↓↓', color: 'text-wine-400/80', animate: '' };
        case 'static':
        default:
            return { symbol: '→', color: 'text-dark-500', animate: '' };
    }
};

const PopularityBadge = ({ popularity, velocity, compact = false }) => {
    const tier = getTier(popularity);
    const vel = getVelocity(velocity);

    if (compact) {
        // Ultra-compact: just arrow with color
        return (
            <span
                className={`inline-flex items-center gap-0.5 ${tier.color}`}
                title={`${tier.label} (${velocity || 'static'})`}
            >
                <span className="text-[10px] font-bold uppercase tracking-tight">{tier.label}</span>
                <span className={`${vel.color} ${vel.animate} text-xs font-bold`}>{vel.symbol}</span>
            </span>
        );
    }

    return (
        <div
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${tier.bg} border border-white/5`}
            title={`Popularity: ${popularity || 50}/100 | Trend: ${velocity || 'static'}`}
        >
            <span className={`text-xs font-semibold ${tier.color}`}>
                {tier.label}
            </span>
            <span className={`${vel.color} ${vel.animate} text-sm font-bold leading-none`}>
                {vel.symbol}
            </span>
        </div>
    );
};

export default PopularityBadge;
